import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ILeadDTO } from '@shared/common';

/**
 * Client for captured leads.
 *
 * Tenant-facing reads/writes route through the lobby service dashboard surface (`dashboard/:tenantID/leads`).
 */
export class LeadClient {
  /** Singleton instance. */
  private static instance: LeadClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton LeadClient instance. */
  public static GetInstance(): LeadClient {
    return LeadClient.instance ?? (LeadClient.instance = new LeadClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/leads - List a tenant's captured leads, newest first, with optional free-text search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (name/phone/email/reason).
   * @returns The tenant's leads page, most recent first.
   */
  public async ListLeads(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<ILeadDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/leads?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch leads');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/leads/count - Count a tenant's leads matching an optional free-text search term.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param pattern - Optional free-text search term.
   * @returns The number of matching leads.
   */
  public async CountLeads(signal: AbortSignal, tenantID: string, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/leads/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch lead count');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/leads/:leadID/resolve - Mark a lead as contacted/resolved.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param leadID - The lead to resolve.
   * @returns The updated lead.
   */
  public async ResolveLead(signal: AbortSignal, tenantID: string, leadID: string): Promise<ILeadDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/leads/${leadID}/resolve`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to resolve lead');
    return await res.json();
  }
}
